import { apiUrl } from './config';

export type ApiError = { error: string; status: number };

function getToken(): string | null {
  // Preview tabs (sessionStorage) take precedence over the regular
  // localStorage session, see lib/auth.tsx.
  const preview = typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem('aotv.preview-session')
    : null;
  if (preview) {
    try {
      return (JSON.parse(preview) as { token?: string }).token ?? null;
    } catch {
      sessionStorage.removeItem('aotv.preview-session');
    }
  }
  const raw = localStorage.getItem('aotv.session');
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { token?: string }).token ?? null;
  } catch {
    return null;
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(apiUrl(path), { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = body as { error?: string } | null;
    throw { error: err?.error ?? res.statusText, status: res.status } satisfies ApiError;
  }
  return body as T;
}

/**
 * Authenticated file download. Fetches the path with the session
 * Bearer token, reads the response as a Blob, and triggers a browser
 * download with the filename from Content-Disposition (or the
 * fallback supplied). Used for things like the Blackmagic XML export.
 */
export async function apiDownload(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(apiUrl(path), { headers });
  if (!res.ok) {
    const text = await res.text();
    let err: { error?: string } | null = null;
    try {
      err = text ? (JSON.parse(text) as { error?: string }) : null;
    } catch {
      err = { error: text || res.statusText };
    }
    throw { error: err?.error ?? res.statusText, status: res.status } satisfies ApiError;
  }

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type EventRow = {
  id: string;
  title: string;
  date: string | null;
  status: 'draft' | 'live' | 'ended';
  created_at: number;
  embed_code: string | null;
  rehearsal_vmix_id: string | null;
  assigned_to: string | null;
  assignee_name?: string | null;
};

export type StreamPlatform =
  | 'youtube'
  | 'vimeo'
  | 'facebook'
  | 'twitch'
  | 'linkedin'
  | 'custom';

export type StreamingDestinationRow = {
  id: string;
  event_id: string | null;
  event_title?: string | null;
  name: string;
  platform: StreamPlatform;
  rtmp_url: string;
  stream_key: string;
  backup_url: string | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
};

export type SpeakerRow = {
  id: string;
  name: string;
  email: string;
  created_at: number;
  has_password: boolean;
};

export type SpeakerRole = 'speaker' | 'host' | 'panellist' | 'moderator';

export type TeamRole = 'super_admin' | 'admin';

export type TeamMemberRow = {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  has_password: boolean;
  created_at: number;
};

export type Me = {
  teamMemberId: string;
  email: string;
  role: TeamRole;
};

export const SPEAKER_ROLES: SpeakerRole[] = ['speaker', 'host', 'panellist', 'moderator'];

export type SlotSourceType = 'vmix_call' | 'zoom';

export type SlotRow = {
  id: string;
  event_id: string;
  vmix_id: string | null;
  speaker_id: string | null;
  order_index: number;
  source_type: SlotSourceType;
  zoom_meeting_id: string | null;
  zoom_passcode: string | null;
};

export type EventSpeakerRow = {
  id: string;
  event_id: string;
  speaker_id: string;
  status: 'invited' | 'active' | 'done';
  role: SpeakerRole;
  slide_control_key: string | null;
  slide_control_enabled: 0 | 1;
  slide_license_id: string | null;
  slide_license_name?: string | null;
  slide_license_key?: string | null;
  note: string | null;
  is_hand_raised: 0 | 1;
};

export type SlideLicenseRow = {
  id: string;
  name: string;
  key: string;
  created_at: number;
};

export type SlideLicenseConnection = {
  id: string;
  name: string;
  primary: boolean;
  connectedAt: number;
  slideNumber: number | null;
  totalSlides: number | null;
};

export type EventSpeakerWithProfile = EventSpeakerRow & { name: string; email: string };

export type SrtMode = 'listener' | 'caller' | 'push' | 'pull';

export type SrtConfigRow = {
  id: string;
  event_id: string | null;
  event_title?: string | null;
  name: string;
  mode: SrtMode;
  url: string;
  stream_id: string | null;
  passphrase: string | null;
  latency_ms: number | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
};

export type EventDetail = EventRow & {
  speakers: EventSpeakerWithProfile[];
  slots: SlotRow[];
};
